import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../services/api";
import Brand from "../components/Brand";

type LoginUser = {
  id: number;
  email: string;
  role: "user" | "admin";
  plan: string;
  credits: number;
  subscription_status: string;
};

export default function LoginPage({
  onLogin,
  initialMode = "login",
  admin = false,
}: {
  onLogin: (user: LoginUser) => void;
  initialMode?: "login" | "register";
  admin?: boolean;
}) {
  const mode = admin ? "login" : initialMode;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const affiliate = params.get("next") === "affiliate";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");
    try {
      const response = await apiFetch(
        `/api/auth/${admin ? "admin-login" : mode === "register" ? "register" : "login"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        },
      );
      const payload = await response.json();
      if (!response.ok) {
        setError(
          typeof payload.error === "string"
            ? payload.error
            : "Please check your email and password.",
        );
        return;
      }
      onLogin(payload.user);
      navigate(
        admin ? "/admin" : affiliate ? "/affiliate" : mode === "register" ? "/pricing" : "/dashboard",
      );
    } catch {
      setError("Unable to connect. Please try again.");
    }
  };

  return (
    <div className="login-page">
      <header className="header">
        <Brand admin={admin} />
      </header>

      <main className="login-main">
        <div className="login-card">
          <h1>
            {admin
              ? "Admin sign in"
              : mode === "register"
                ? "Create your account"
                : "Welcome Back"}
          </h1>
          <p className="subtitle">
            {admin
              ? "Owner access to manage the shared lead database."
              : mode === "register"
                ? "Start finding the right business contacts"
                : "Sign in to your account"}
          </p>

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                autoComplete={
                  mode === "register" ? "new-password" : "current-password"
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {mode === "register" && <p className="signup-legal">By creating an account, you agree to the <Link to="/terms">Terms of Service</Link> and acknowledge the <Link to="/privacy">Privacy Policy</Link>.</p>}
            <button type="submit" className="btn-primary btn-full">
              {mode === "register" ? "Create account" : "Sign In"}
            </button>
          </form>

          {admin ? (
            <p className="footer-text">
              <Link to="/">Visit ReachAtlas</Link>
            </p>
          ) : (
            <p className="footer-text">
              {mode === "register"
                ? "Already have an account? "
                : "Don't have an account? "}
              <Link to={(mode === "register" ? "/login" : "/signup") + (affiliate ? "?next=affiliate" : "")}>
                {mode === "register" ? "Sign in" : "Create one"}
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
